import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../App';
import { getApiUrl } from '../config/api';

const ContractList = () => {
  const { hasPermission } = useAuth();
  const [contracts, setContracts] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingContract, setEditingContract] = useState(null);
  const [viewingContract, setViewingContract] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedContract, setSelectedContract] = useState(null);
  const [paymentSchedules, setPaymentSchedules] = useState([]);
  const [showAddPaymentModal, setShowAddPaymentModal] = useState(false);
  const [paymentFormData, setPaymentFormData] = useState({
    phase_name: '',
    amount: '',
    due_date: '',
    status: 'PENDING',
    notes: ''
  });
  const [formData, setFormData] = useState({
    project_id: '',
    contract_number: '',
    total_amount: '',
    signed_date: '',
    status: 'DRAFT',
    file_path: ''
  });

  useEffect(() => {
  // eslint-disable-next-line react-hooks/exhaustive-deps
    fetchContracts();
    fetchProjects();
  }, []);

  useEffect(() => {
  // eslint-disable-next-line react-hooks/exhaustive-deps
    // Debounce search
    const delayedSearch = setTimeout(() => {
      fetchContracts();
    }, 500);

    return () => clearTimeout(delayedSearch);
  }, [searchTerm, statusFilter]);

  const fetchContracts = async () => {
    try {
      setLoading(true);
      const params = {};
      if (searchTerm) params.search = searchTerm;
      if (statusFilter !== 'all') params.status = statusFilter;
      
      const response = await axios.get(getApiUrl('/contracts'), { params });
      console.log('Contracts API response:', response.data);
      
      if (Array.isArray(response.data)) {
        setContracts(response.data);
      } else {
        console.warn('Contracts API response is not an array:', response.data);
        setContracts([]);
      }
    } catch (error) {
      console.error('Failed to fetch contracts:', error);
      if (error.response?.status === 403) {
        alert('Bạn không có quyền xem danh sách hợp đồng');
      } else {
        alert('Lỗi tải danh sách hợp đồng');
      }
      setContracts([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchProjects = async () => {
    try {
      const response = await axios.get(getApiUrl('/projects/list/simple'));
      setProjects(response.data);
    } catch (error) {
      console.error('Failed to fetch projects:', error);
    }
  };

  const handleCreateSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = {
        ...formData,
        total_amount: parseFloat(formData.total_amount),
        project_id: parseInt(formData.project_id),
        signed_date: formData.signed_date || null,
        // Auto-generate contract number if empty
        contract_number: formData.contract_number || 
          `CT-${new Date().getFullYear()}-${String(Date.now()).slice(-6)}`
      };

      console.log('Submitting contract data:', submitData);
      
      await axios.post(getApiUrl('/contracts'), submitData);
      setShowCreateModal(false);
      resetForm();
      fetchContracts();
      alert('Tạo hợp đồng thành công!');
    } catch (error) {
      console.error('Failed to create contract:', error);
      console.error('Error response:', error.response);
      
      if (error.response?.data?.detail) {
        if (Array.isArray(error.response.data.detail)) {
          // Validation errors from Pydantic
          const errorMessages = error.response.data.detail.map(err => 
            `${err.loc?.join('.')}: ${err.msg}`
          ).join('\n');
          alert(`Lỗi validation:\n${errorMessages}`);
        } else {
          alert(`Lỗi tạo hợp đồng: ${error.response.data.detail}`);
        }
      } else {
        alert('Lỗi tạo hợp đồng. Vui lòng kiểm tra console để xem chi tiết.');
      }
    }
  };

  const handleUpdateSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = {
        ...formData,
        total_amount: parseFloat(formData.total_amount),
        project_id: parseInt(formData.project_id),
        signed_date: formData.signed_date || null
      };

      await axios.put(getApiUrl(`/contracts/${editingContract.id}`), submitData);
      setEditingContract(null);
      resetForm();
      fetchContracts();
      alert('Cập nhật hợp đồng thành công!');
    } catch (error) {
      console.error('Failed to update contract:', error);
      if (error.response?.data?.detail) {
        alert(`Lỗi cập nhật hợp đồng: ${error.response.data.detail}`);
      } else {
        alert('Lỗi cập nhật hợp đồng');
      }
    }
  };

  const handleDelete = async (contractId) => {
    if (window.confirm('Bạn có chắc chắn muốn xóa hợp đồng này?')) {
      try {
        await axios.delete(getApiUrl(`/contracts/${contractId}`));
        fetchContracts();
        alert('Xóa hợp đồng thành công!');
      } catch (error) {
        console.error('Failed to delete contract:', error);
        if (error.response?.data?.detail) {
          alert(`Lỗi xóa hợp đồng: ${error.response.data.detail}`);
        } else {
          alert('Lỗi xóa hợp đồng');
        }
      }
    }
  };

  const resetForm = () => {
    setFormData({
      project_id: '',
      contract_number: '',
      total_amount: '',
      signed_date: '',
      status: 'DRAFT',
      file_path: ''
    });
  };

  const openEditModal = (contract) => {
    setEditingContract(contract);
    setFormData({
      project_id: contract.project_id?.toString() || '',
      contract_number: contract.contract_number || '',
      total_amount: contract.total_amount?.toString() || '',
      signed_date: contract.signed_date || '',
      status: contract.status || 'DRAFT',
      file_path: contract.file_path || ''
    });
  };

  const openViewModal = async (contract) => {
    try {
      const response = await axios.get(getApiUrl(`/contracts/${contract.id}`));
      setViewingContract(response.data);
    } catch (error) {
      console.error('Failed to fetch contract details:', error);
      setViewingContract(contract);
    }
  };

  const openPaymentModal = async (contract) => {
    setSelectedContract(contract);
    try {
      const response = await axios.get(getApiUrl(`/payment-schedules/contract/${contract.id}`));
      setPaymentSchedules(response.data);
    } catch (error) {
      console.error('Failed to fetch payment schedules:', error);
      setPaymentSchedules([]);
    }
    setShowPaymentModal(true);
  };

  const getTotalScheduledAmount = () => {
    if (!Array.isArray(paymentSchedules)) return 0;
    const total = paymentSchedules.reduce((sum, schedule) => sum + (Number(schedule?.amount) || 0), 0);
    return isNaN(total) ? 0 : total;
  };

  const canAddMoreSchedules = () => {
    if (!selectedContract) return false;
    const currentTotal = getTotalScheduledAmount();
    const contractAmount = selectedContract.total_amount || 0;
    return currentTotal < contractAmount;
  };

  const getRemainingAmount = () => {
    if (!selectedContract) return 0;
    const currentTotal = Number(getTotalScheduledAmount()) || 0;
    const contractAmount = Number(selectedContract.total_amount) || 0;
    const remaining = contractAmount - currentTotal;
    if (isNaN(remaining) || remaining < 0) return 0;
    return remaining;
  };

  const handleAddPaymentSchedule = async (e) => {
    e.preventDefault();
    
    try {
      const newAmount = parseFloat(paymentFormData.amount);
      const currentTotal = getTotalScheduledAmount();
      const contractAmount = selectedContract.total_amount || 0;
      
      // Validation: Kiểm tra tổng số tiền không vượt quá hợp đồng
      if (currentTotal + newAmount > contractAmount) {
        const remaining = contractAmount - currentTotal;
        alert(`Lỗi: Tổng số tiền lịch thanh toán không được vượt quá giá trị hợp đồng.\nSố tiền còn lại có thể thêm: ${formatCurrency(remaining)}`);
        return;
      }

      const submitData = {
        ...paymentFormData,
        contract_id: selectedContract.id,
        amount: newAmount,
        due_date: paymentFormData.due_date || null
      };

      await axios.post(getApiUrl('/payment-schedules'), submitData);
      setShowAddPaymentModal(false);
      resetPaymentForm();
      // Refresh payment schedules
      const response = await axios.get(getApiUrl(`/payment-schedules/contract/${selectedContract.id}`));
      setPaymentSchedules(response.data);
      // Refresh contracts list to update progress
      fetchContracts();
      alert('Thêm lịch thanh toán thành công!');
    } catch (error) {
      console.error('Failed to add payment schedule:', error);
      if (error.response?.data?.detail) {
        alert(`Lỗi thêm lịch thanh toán: ${error.response.data.detail}`);
      } else {
        alert('Lỗi thêm lịch thanh toán');
      }
    }
  };

  const handleMarkAsPaid = async (scheduleId) => {
    try {
      await axios.patch(getApiUrl(`/payment-schedules/${scheduleId}/mark-paid`));
      // Refresh payment schedules
      const response = await axios.get(getApiUrl(`/payment-schedules/contract/${selectedContract.id}`));
      setPaymentSchedules(response.data);
      // Refresh contracts list to update progress
      fetchContracts();
      alert('Đánh dấu thanh toán thành công!');
    } catch (error) {
      console.error('Failed to mark as paid:', error);
      alert('Lỗi cập nhật trạng thái thanh toán');
    }
  };

  const handleDeletePaymentSchedule = async (scheduleId) => {
    if (window.confirm('Bạn có chắc chắn muốn xóa lịch thanh toán này?')) {
      try {
        await axios.delete(getApiUrl(`/payment-schedules/${scheduleId}`));
        // Refresh payment schedules
        const response = await axios.get(getApiUrl(`/payment-schedules/contract/${selectedContract.id}`));
        setPaymentSchedules(response.data);
        // Refresh contracts list to update progress
        fetchContracts();
        alert('Xóa lịch thanh toán thành công!');
      } catch (error) {
        console.error('Failed to delete payment schedule:', error);
        if (error.response?.data?.detail) {
          alert(`Lỗi xóa lịch thanh toán: ${error.response.data.detail}`);
        } else {
          alert('Lỗi xóa lịch thanh toán');
        }
      }
    }
  };

  const resetPaymentForm = () => {
    setPaymentFormData({
      phase_name: '',
      amount: '',
      due_date: '',
      status: 'PENDING',
      notes: ''
    });
  };

  const getStatusColor = (status) => {
    switch (status?.toUpperCase()) {
      case 'SIGNED': return 'bg-green-100 text-green-800';
      case 'COMPLETED': return 'bg-blue-100 text-blue-800';
      case 'DRAFT': return 'bg-yellow-100 text-yellow-800';
      case 'CANCELLED': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status) => {
    switch (status?.toUpperCase()) {
      case 'SIGNED': return 'Đã ký';
      case 'COMPLETED': return 'Hoàn thành';
      case 'DRAFT': return 'Bản nháp';
      case 'CANCELLED': return 'Đã hủy';
      default: return 'Không xác định';
    }
  };

  const getPaymentStatusColor = (status) => {
    switch (status?.toUpperCase()) {
      case 'PAID': return 'bg-green-100 text-green-800';
      case 'PENDING': return 'bg-yellow-100 text-yellow-800';
      case 'OVERDUE': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getPaymentStatusText = (status) => {
    switch (status?.toUpperCase()) {
      case 'PAID': return 'Đã thanh toán';
      case 'PENDING': return 'Chờ thanh toán';
      case 'OVERDUE': return 'Quá hạn';
      default: return 'Không xác định';
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('vi-VN', {
      style: 'currency',
      currency: 'VND'
    }).format(amount);
  };

  const calculateProgress = (paymentSchedules) => {
    if (!Array.isArray(paymentSchedules) || paymentSchedules.length === 0) return 0;
    const totalSchedules = paymentSchedules.length;
    const paidSchedules = paymentSchedules.filter(schedule => schedule?.status?.toUpperCase() === 'PAID').length;
    return (paidSchedules / totalSchedules) * 100;
  };

  const calculateProgressByAmount = (paymentSchedules) => {
    if (!Array.isArray(paymentSchedules)) return 0;
    const totalAmount = paymentSchedules.reduce((sum, schedule) => sum + (schedule?.amount || 0), 0);
    const paidAmount = paymentSchedules
      .filter(schedule => schedule?.status?.toUpperCase() === 'PAID')
      .reduce((sum, schedule) => sum + (schedule?.amount || 0), 0);
    return totalAmount > 0 ? (paidAmount / totalAmount) * 100 : 0;
  };

  const getProgressColor = (progress) => {
    if (progress === 100) return 'bg-green-500';
    if (progress >= 75) return 'bg-blue-500';
    if (progress >= 50) return 'bg-yellow-500';
    if (progress >= 25) return 'bg-orange-500';
    return 'bg-red-500';
  };

  const getPaymentStats = () => {
    const totalContracts = filteredContracts.length;
    const contractsWithPayments = filteredContracts.filter(c => c.payment_schedules && c.payment_schedules.length > 0);
    const fullyPaidContracts = contractsWithPayments.filter(c => calculateProgress(c.payment_schedules) === 100);
    const partiallyPaidContracts = contractsWithPayments.filter(c => {
      const progress = calculateProgress(c.payment_schedules);
      return progress > 0 && progress < 100;
    });
    const unpaidContracts = contractsWithPayments.filter(c => calculateProgress(c.payment_schedules) === 0);

    const totalValue = filteredContracts.reduce((sum, contract) => sum + (contract.total_amount || 0), 0);
    const paidValue = contractsWithPayments.reduce((sum, contract) => {
      const progressByAmount = calculateProgressByAmount(contract.payment_schedules);
      return sum + ((contract.total_amount || 0) * progressByAmount / 100);
    }, 0);

    return {
      totalContracts,
      fullyPaidContracts: fullyPaidContracts.length,
      partiallyPaidContracts: partiallyPaidContracts.length,
      unpaidContracts: unpaidContracts.length,
      totalValue,
      paidValue,
      overallProgress: totalValue > 0 ? (paidValue / totalValue) * 100 : 0
    };
  };

  const filteredContracts = (contracts || []).filter(contract => {
    const contractNumber = contract?.contract_number || '';
    const projectName = contract?.project?.name || '';
    const companyName = contract?.project?.customer?.company_name || '';
    
    const matchesSearch = contractNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         projectName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         companyName.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || contract?.status?.toUpperCase() === statusFilter.toUpperCase();
    return matchesSearch && matchesStatus;
  });

  // Check permission to access this page
  if (!hasPermission('finance', 'read')) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="text-6xl mb-4">🔒</div>
          <h2 className="text-xl font-semibold text-gray-700 mb-2">Không có quyền truy cập</h2>
          <p className="text-gray-500">Bạn không có quyền xem danh sách hợp đồng.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Quản lý hợp đồng</h1>
          <p className="text-gray-600">Theo dõi hợp đồng và thanh toán</p>
        </div>
        {hasPermission('finance', 'write') && (
          <button 
            onClick={() => setShowCreateModal(true)}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            <span>Tạo hợp đồng</span>
          </button>
        )}
      </div>

      {/* Payment Statistics */}
      {filteredContracts.length > 0 && (() => {
        const stats = getPaymentStats();
        return (
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Tổng quan thanh toán</h3>
            
            {/* Overall Progress */}
            <div className="mb-6">
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm text-gray-600">Tiến độ thanh toán tổng thể</span>
                <span className="text-sm font-medium text-gray-900">{stats.overallProgress.toFixed(1)}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3">
                <div 
                  className={`h-3 rounded-full transition-all duration-300 ${getProgressColor(stats.overallProgress)}`}
                  style={{ width: `${stats.overallProgress}%` }}
                ></div>
              </div>
              <div className="flex justify-between items-center mt-2 text-sm text-gray-600">
                <span>Đã thu: {formatCurrency(stats.paidValue)}</span>
                <span>Tổng giá trị: {formatCurrency(stats.totalValue)}</span>
              </div>
            </div>

            {/* Contract Status Distribution */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center p-3 bg-green-50 rounded-lg">
                <div className="text-2xl font-bold text-green-600">{stats.fullyPaidContracts}</div>
                <div className="text-sm text-green-600">Đã thanh toán đủ</div>
              </div>
              <div className="text-center p-3 bg-yellow-50 rounded-lg">
                <div className="text-2xl font-bold text-yellow-600">{stats.partiallyPaidContracts}</div>
                <div className="text-sm text-yellow-600">Thanh toán một phần</div>
              </div>
              <div className="text-center p-3 bg-red-50 rounded-lg">
                <div className="text-2xl font-bold text-red-600">{stats.unpaidContracts}</div>
                <div className="text-sm text-red-600">Chưa thanh toán</div>
              </div>
              <div className="text-center p-3 bg-blue-50 rounded-lg">
                <div className="text-2xl font-bold text-blue-600">{stats.totalContracts}</div>
                <div className="text-sm text-blue-600">Tổng hợp đồng</div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <input
              type="text"
              placeholder="Tìm kiếm hợp đồng..."
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <select
            className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">Tất cả trạng thái</option>
            <option value="DRAFT">Bản nháp</option>
            <option value="SIGNED">Đã ký</option>
            <option value="COMPLETED">Hoàn thành</option>
            <option value="CANCELLED">Đã hủy</option>
          </select>
        </div>
      </div>

      {/* Tổng giá trị hợp đồng */}
      <div className="text-right font-bold text-blue-700 mb-2">
        Tổng giá trị hợp đồng: {formatCurrency(filteredContracts.reduce((sum, c) => sum + (Number(c.total_amount) || 0), 0))}
      </div>

      {/* Contracts List */}
      <div className="space-y-4">
        {filteredContracts.map((contract) => {
          const progress = calculateProgress(contract.payment_schedules);
          const progressByAmount = calculateProgressByAmount(contract.payment_schedules);
          const totalSchedules = contract?.payment_schedules?.length || 0;
          const paidSchedules = contract?.payment_schedules?.filter(s => s?.status?.toUpperCase() === 'PAID').length || 0;
          
          return (
            <div key={contract.id} className="bg-white rounded-lg shadow p-6">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="text-lg font-medium text-gray-900">
                    {contract.contract_number}
                  </h3>
                  <p className="text-sm text-gray-600">{contract.project.name}</p>
                  <p className="text-sm text-gray-500">{contract.project.customer.company_name}</p>
                </div>
                <div className="text-right">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(contract.status)}`}>
                    {getStatusText(contract.status)}
                  </span>
                  <p className="text-lg font-semibold text-gray-900 mt-1">
                    {formatCurrency(contract.total_amount)}
                  </p>
                  {contract.signed_date && (
                    <p className="text-sm text-gray-500">Ký: {contract.signed_date}</p>
                  )}
                </div>
              </div>

              {/* Payment Progress */}
              <div className="mb-4">
                <div className="flex justify-between items-center mb-2">
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-gray-600">Tiến độ thanh toán</span>
                    <span className="text-xs text-gray-500">
                      ({paidSchedules}/{totalSchedules} đợt)
                    </span>
                  </div>
                  <div className="text-right">
                    <span className="text-sm font-medium text-gray-900">{progress.toFixed(0)}%</span>
                    <span className="text-xs text-gray-500 ml-2">
                      ({progressByAmount.toFixed(0)}% theo giá trị)
                    </span>
                  </div>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-3">
                  <div 
                    className={`h-3 rounded-full transition-all duration-300 ${getProgressColor(progress)}`}
                    style={{ width: `${progress}%` }}
                  ></div>
                </div>
                {totalSchedules > 0 && (
                  <div className="flex justify-between items-center mt-1 text-xs text-gray-500">
                    <span>Đã thanh toán: {paidSchedules} đợt</span>
                    <span>Còn lại: {totalSchedules - paidSchedules} đợt</span>
                  </div>
                )}
              </div>

              {/* Payment Schedules */}
              <div className="border-t pt-4">
                <h4 className="text-sm font-medium text-gray-900 mb-3">Lịch thanh toán</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {(contract?.payment_schedules || []).map((schedule, index) => (
                    <div key={index} className="border rounded-lg p-3">
                      <div className="flex justify-between items-start mb-2">
                        <h5 className="text-sm font-medium text-gray-900">
                          {schedule?.phase_name || `Đợt ${index + 1}`}
                        </h5>
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getPaymentStatusColor(schedule?.status)}`}>
                          {getPaymentStatusText(schedule?.status)}
                        </span>
                      </div>
                      <p className="text-sm font-semibold text-gray-900">
                        {formatCurrency(schedule?.amount || 0)}
                      </p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Actions */}
              <div className="flex justify-end space-x-2 mt-4 pt-4 border-t">
                <button 
                  onClick={() => openViewModal(contract)}
                  className="text-blue-600 hover:text-blue-900 p-2 rounded"
                  title="Xem chi tiết"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                </button>
                {hasPermission('finance', 'write') && (
                  <>
                    <button 
                      onClick={() => openPaymentModal(contract)}
                      className="text-purple-600 hover:text-purple-900 p-2 rounded"
                      title="Quản lý thanh toán"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                      </svg>
                    </button>
                    <button 
                      onClick={() => openEditModal(contract)}
                      className="text-green-600 hover:text-green-900 p-2 rounded"
                      title="Chỉnh sửa"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </button>
                    <button 
                      onClick={() => handleDelete(contract.id)}
                      className="text-red-600 hover:text-red-900 p-2 rounded"
                      title="Xóa"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {filteredContracts.length === 0 && (
        <div className="text-center py-12">
          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-gray-900">Không có hợp đồng nào</h3>
          <p className="mt-1 text-sm text-gray-500">
            {searchTerm || statusFilter !== 'all' ? 'Không tìm thấy hợp đồng phù hợp' : 'Bắt đầu bằng cách tạo hợp đồng mới.'}
          </p>
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Tạo hợp đồng mới</h3>
              <form onSubmit={handleCreateSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Dự án *</label>
                  <select
                    required
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    value={formData.project_id}
                    onChange={(e) => setFormData({ ...formData, project_id: e.target.value })}
                  >
                    <option value="">Chọn dự án</option>
                    {projects.map(project => (
                      <option key={project.id} value={project.id}>
                        {project.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Số hợp đồng</label>
                  <input
                    type="text"
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    value={formData.contract_number}
                    onChange={(e) => setFormData({ ...formData, contract_number: e.target.value })}
                    placeholder="Tự động tạo nếu để trống"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Tổng giá trị *</label>
                  <input
                    type="number"
                    required
                    min="0"
                    step="0.01"
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    value={formData.total_amount}
                    onChange={(e) => setFormData({ ...formData, total_amount: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Ngày ký</label>
                  <input
                    type="date"
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    value={formData.signed_date}
                    onChange={(e) => setFormData({ ...formData, signed_date: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Trạng thái</label>
                  <select
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  >
                    <option value="DRAFT">Bản nháp</option>
                    <option value="SIGNED">Đã ký</option>
                    <option value="COMPLETED">Hoàn thành</option>
                    <option value="CANCELLED">Đã hủy</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Đường dẫn file</label>
                  <input
                    type="text"
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    value={formData.file_path}
                    onChange={(e) => setFormData({ ...formData, file_path: e.target.value })}
                    placeholder="URL hoặc đường dẫn file hợp đồng"
                  />
                </div>
                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowCreateModal(false);
                      resetForm();
                    }}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200"
                  >
                    Hủy
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700"
                  >
                    Tạo
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {editingContract && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Chỉnh sửa hợp đồng</h3>
              <form onSubmit={handleUpdateSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Dự án *</label>
                  <select
                    required
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    value={formData.project_id}
                    onChange={(e) => setFormData({ ...formData, project_id: e.target.value })}
                  >
                    <option value="">Chọn dự án</option>
                    {projects.map(project => (
                      <option key={project.id} value={project.id}>
                        {project.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Số hợp đồng</label>
                  <input
                    type="text"
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    value={formData.contract_number}
                    onChange={(e) => setFormData({ ...formData, contract_number: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Tổng giá trị *</label>
                  <input
                    type="number"
                    required
                    min="0"
                    step="0.01"
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    value={formData.total_amount}
                    onChange={(e) => setFormData({ ...formData, total_amount: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Ngày ký</label>
                  <input
                    type="date"
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    value={formData.signed_date}
                    onChange={(e) => setFormData({ ...formData, signed_date: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Trạng thái</label>
                  <select
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  >
                    <option value="DRAFT">Bản nháp</option>
                    <option value="SIGNED">Đã ký</option>
                    <option value="COMPLETED">Hoàn thành</option>
                    <option value="CANCELLED">Đã hủy</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Đường dẫn file</label>
                  <input
                    type="text"
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    value={formData.file_path}
                    onChange={(e) => setFormData({ ...formData, file_path: e.target.value })}
                  />
                </div>
                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setEditingContract(null);
                      resetForm();
                    }}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200"
                  >
                    Hủy
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700"
                  >
                    Cập nhật
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* View Modal */}
      {viewingContract && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-2xl max-w-2xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Chi tiết hợp đồng</h3>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Số hợp đồng</label>
                    <p className="mt-1 text-sm text-gray-900">{viewingContract.contract_number || 'Chưa có'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Trạng thái</label>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium mt-1 ${getStatusColor(viewingContract.status)}`}>
                      {getStatusText(viewingContract.status)}
                    </span>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Dự án</label>
                  <p className="mt-1 text-sm text-gray-900">{viewingContract.project?.name}</p>
                  <p className="text-sm text-gray-500">{viewingContract.project?.customer?.company_name}</p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Tổng giá trị</label>
                    <p className="mt-1 text-lg font-semibold text-gray-900">{formatCurrency(viewingContract.total_amount)}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Ngày ký</label>
                    <p className="mt-1 text-sm text-gray-900">
                      {viewingContract.signed_date ? new Date(viewingContract.signed_date).toLocaleDateString('vi-VN') : 'Chưa ký'}
                    </p>
                  </div>
                </div>
                {viewingContract.file_path && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">File hợp đồng</label>
                    <p className="mt-1 text-sm text-blue-600 hover:text-blue-800">
                      <a href={viewingContract.file_path} target="_blank" rel="noopener noreferrer">
                        Xem file
                      </a>
                    </p>
                  </div>
                )}
                {viewingContract.payment_schedules && viewingContract.payment_schedules.length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Lịch thanh toán</label>
                    <div className="border rounded-lg p-3 max-h-40 overflow-y-auto">
                      {viewingContract.payment_schedules.map((schedule, index) => (
                        <div key={index} className="flex justify-between items-center py-2 border-b last:border-b-0">
                          <div>
                            <p className="text-sm font-medium">{schedule.phase_name}</p>
                            <p className="text-sm text-gray-500">{formatCurrency(schedule.amount)}</p>
                          </div>
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getPaymentStatusColor(schedule.status)}`}>
                            {getPaymentStatusText(schedule.status)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                <div className="flex justify-end pt-4">
                  <button
                    onClick={() => setViewingContract(null)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200"
                  >
                    Đóng
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Payment Schedule Management Modal */}
      {showPaymentModal && selectedContract && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-40">
          <div className="relative top-20 mx-auto p-5 border w-4xl max-w-4xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  Quản lý thanh toán - {selectedContract.contract_number}
                </h3>
                <div className="flex flex-col items-end space-y-2">
                  <div className="text-sm text-gray-600">
                    <span>Tổng đã lên lịch: {formatCurrency(getTotalScheduledAmount())}</span>
                    <span className="mx-2">|</span>
                    <span>Giá trị hợp đồng: {formatCurrency(selectedContract.total_amount)}</span>
                  </div>
                  {canAddMoreSchedules() ? (
                    <div className="text-xs text-blue-600 mb-2">
                      Còn lại: {formatCurrency(getRemainingAmount())}
                    </div>
                  ) : (
                    <div className="text-xs text-red-600 mb-2">
                      Đã đủ số tiền hợp đồng
                    </div>
                  )}
                  <button
                    onClick={() => setShowAddPaymentModal(true)}
                    disabled={!canAddMoreSchedules()}
                    className={`px-3 py-1 rounded text-sm transition-colors ${
                      canAddMoreSchedules()
                        ? 'bg-blue-600 text-white hover:bg-blue-700'
                        : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    }`}
                    title={canAddMoreSchedules() ? 'Thêm lịch thanh toán' : 'Không thể thêm - đã đủ số tiền hợp đồng'}
                  >
                    + Thêm lịch thanh toán
                  </button>
                </div>
              </div>
              
              <div className="space-y-4 max-h-96 overflow-y-auto">
                {paymentSchedules.map((schedule) => (
                  <div key={schedule.id} className="border rounded-lg p-4 bg-gray-50">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <h4 className="font-medium text-gray-900">{schedule.phase_name}</h4>
                        <p className="text-lg font-semibold text-gray-900 mt-1">
                          {formatCurrency(schedule.amount)}
                        </p>
                        {schedule.due_date && (
                          <p className="text-sm text-gray-600">
                            Hạn: {new Date(schedule.due_date).toLocaleDateString('vi-VN')}
                          </p>
                        )}
                        {schedule.notes && (
                          <p className="text-sm text-gray-600 mt-1">{schedule.notes}</p>
                        )}
                        {schedule.paid_at && (
                          <p className="text-sm text-green-600 mt-1">
                            Đã thanh toán: {new Date(schedule.paid_at).toLocaleDateString('vi-VN')}
                          </p>
                        )}
                      </div>
                      <div className="flex flex-col items-end space-y-2">
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getPaymentStatusColor(schedule.status)}`}>
                          {getPaymentStatusText(schedule.status)}
                        </span>
                        <div className="flex space-x-2">
                          {schedule.status !== 'PAID' && (
                            <button
                              onClick={() => handleMarkAsPaid(schedule.id)}
                              className="text-green-600 hover:text-green-800 text-xs"
                            >
                              Đánh dấu đã thanh toán
                            </button>
                          )}
                          <button
                            onClick={() => handleDeletePaymentSchedule(schedule.id)}
                            className="text-red-600 hover:text-red-800 text-xs"
                          >
                            Xóa
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
                
                {paymentSchedules.length === 0 && (
                  <div className="text-center py-8">
                    <p className="text-gray-500">Chưa có lịch thanh toán nào</p>
                  </div>
                )}
              </div>
              
              <div className="flex justify-end pt-4 mt-4 border-t">
                <button
                  onClick={() => {
                    setShowPaymentModal(false);
                    setSelectedContract(null);
                    setPaymentSchedules([]);
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200"
                >
                  Đóng
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Payment Schedule Modal */}
      {showAddPaymentModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Thêm lịch thanh toán</h3>
              
              {/* Thông tin về số tiền */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                <div className="text-sm text-blue-800">
                  <div className="flex justify-between">
                    <span>Giá trị hợp đồng:</span>
                    <span className="font-semibold">{formatCurrency(selectedContract.total_amount)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Đã lên lịch:</span>
                    <span className="font-semibold">{formatCurrency(isNaN(getTotalScheduledAmount()) ? 0 : getTotalScheduledAmount())}</span>
                  </div>
                  <div className="flex justify-between border-t border-blue-200 pt-2 mt-2">
                    <span>Số tiền tối đa có thể thêm:</span>
                    <span className="font-bold text-blue-600">{formatCurrency(isNaN(getRemainingAmount()) ? 0 : getRemainingAmount())}</span>
                  </div>
                </div>
              </div>

              <form onSubmit={handleAddPaymentSchedule} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Tên giai đoạn *</label>
                  <input
                    type="text"
                    required
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    value={paymentFormData.phase_name}
                    onChange={(e) => setPaymentFormData({ ...paymentFormData, phase_name: e.target.value })}
                    placeholder="VD: Tạm ứng, Hoàn thành..."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Số tiền *</label>
                  <input
                    type="number"
                    required
                    min="0"
                    max={getRemainingAmount()}
                    step="0.01"
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    value={paymentFormData.amount}
                    onChange={(e) => {
                      const newAmount = parseFloat(e.target.value) || 0;
                      const maxAmount = getRemainingAmount();
                      if (newAmount <= maxAmount) {
                        setPaymentFormData({ ...paymentFormData, amount: e.target.value });
                      } else {
                        alert(`Số tiền không được vượt quá ${formatCurrency(maxAmount)}`);
                      }
                    }}
                    placeholder={`Tối đa: ${formatCurrency(getRemainingAmount())}`}
                  />
                  <div className="text-xs text-gray-500 mt-1 flex justify-between items-center">
                    <span>Số tiền tối đa: {formatCurrency(getRemainingAmount())}</span>
                    {getRemainingAmount() > 0 && (
                      <button
                        type="button"
                        onClick={() => setPaymentFormData({ ...paymentFormData, amount: getRemainingAmount().toString() })}
                        className="text-blue-600 hover:text-blue-800 text-xs underline"
                      >
                        Điền số tiền còn lại
                      </button>
                    )}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Ngày hết hạn</label>
                  <input
                    type="date"
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    value={paymentFormData.due_date}
                    onChange={(e) => setPaymentFormData({ ...paymentFormData, due_date: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Trạng thái</label>
                  <select
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    value={paymentFormData.status}
                    onChange={(e) => setPaymentFormData({ ...paymentFormData, status: e.target.value })}
                  >
                    <option value="PENDING">Chờ thanh toán</option>
                    <option value="PAID">Đã thanh toán</option>
                    <option value="OVERDUE">Quá hạn</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Ghi chú</label>
                  <textarea
                    rows="3"
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    value={paymentFormData.notes}
                    onChange={(e) => setPaymentFormData({ ...paymentFormData, notes: e.target.value })}
                    placeholder="Ghi chú thêm về lịch thanh toán..."
                  />
                </div>
                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddPaymentModal(false);
                      resetPaymentForm();
                    }}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200"
                  >
                    Hủy
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700"
                  >
                    Thêm
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContractList;
